/**
 * Splits text into plain and link segments so a URL inside body copy can be
 * rendered as a tappable link.
 *
 * The pattern mirrors `URL_REGEX` in link-preview.utils (which drives the
 * preview card) but is global, so it finds every URL rather than just the first.
 * Kept separate rather than exported from there because a shared `g` regex
 * carries `lastIndex` between callers, which silently skips matches.
 */

const URL_PATTERN = /https?:\/\/(?:[-\w]+\.)+[\w]{2,}(?:\/[^\s]*)?/gi;

/**
 * Punctuation that reads as sentence structure rather than part of the address.
 * "Read https://example.com." should link the URL and leave the full stop
 * behind. Closing brackets are included for "(see https://example.com)".
 */
const TRAILING_PUNCTUATION = /[.,;:!?'")\]}]+$/;

export type TextSegment = Readonly<{
  text: string;
  /** The href when this segment is a link, otherwise null. */
  url: string | null;
}>;

export const linkifyText = (text: string): TextSegment[] => {
  if (!text) return [];

  const segments: TextSegment[] = [];
  let cursor = 0;

  // Fresh regex per call: a module-level /g/ regex keeps lastIndex across calls.
  const pattern = new RegExp(URL_PATTERN.source, URL_PATTERN.flags);
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    let url = match[0];
    let matchEnd = match.index + url.length;

    // Give trailing punctuation back to the surrounding text.
    const trailing = url.match(TRAILING_PUNCTUATION);
    if (trailing) {
      url = url.slice(0, url.length - trailing[0].length);
      matchEnd -= trailing[0].length;
    }

    // An address that was nothing but punctuation is not a link.
    if (!url) continue;

    if (match.index > cursor) {
      segments.push({ text: text.slice(cursor, match.index), url: null });
    }

    segments.push({ text: url, url });
    cursor = matchEnd;
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), url: null });
  }

  return segments;
};

/** Whether `text` contains at least one link. */
export const hasLink = (text: string): boolean =>
  linkifyText(text).some((segment) => segment.url !== null);
